import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError, type MetricsSnapshot } from "@/api/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Activity, RefreshCw, Users, Radio, Database, Cpu, AlertTriangle, CheckCircle2 } from "lucide-react";

// Admin → Monitor: live operations dashboard. Polls /api/admin/metrics
// every 10s while mounted. Answers three questions at a glance:
//   1. Wieviele Nutzer sind gerade aktiv? (HyperLogLog, letzte 5 min)
//   2. Wieviel API-Traffic läuft, und wieviel davon erroret/drosselt?
//   3. Nähern wir uns einem Limit? (DB-Pool, Event-Loop, RAM, 429s)

const POLL_MS = 10_000;

type Level = "ok" | "warn" | "crit";

function levelCls(l: Level): string {
  return l === "ok"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : l === "warn"
    ? "bg-amber-50 text-amber-700 border-amber-300"
    : "bg-destructive/10 text-destructive border-destructive/40";
}

export default function MonitoringPanel() {
  const [data, setData] = useState<MetricsSnapshot | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.admin.metrics());
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Metriken laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  // Derived figures for the stat cards + limit gauges.
  const derived = useMemo(() => {
    if (!data) return null;
    const last = data.series[data.series.length - 1];
    const last5 = data.series.slice(-5);
    const throttled5m = last5.reduce((s, b) => s + b.s429, 0);
    const errors5m = last5.reduce((s, b) => s + b.s5xx, 0);
    const peak = Math.max(1, ...data.series.map((b) => b.total));
    const worstLag = Math.max(0, ...data.replicas.map((r) => r.elLagMs));
    const worstRss = Math.max(0, ...data.replicas.map((r) => r.rssMb));

    const limits: { label: string; value: string; level: Level; hint: string }[] = [
      {
        label: "Rate-Limiter (429s, letzte 5 min)",
        value: throttled5m === 0 ? "0" : String(throttled5m),
        level: throttled5m === 0 ? "ok" : throttled5m < 20 ? "warn" : "crit",
        hint: throttled5m === 0
          ? `Keine Drosselungen. Limit: ${data.rlMax}/min pro Nutzer.`
          : `Nutzer werden gedrosselt — ggf. RATE_LIMIT_MAX erhöhen (aktuell ${data.rlMax}/min).`,
      },
      {
        label: "Server-Fehler (5xx, letzte 5 min)",
        value: String(errors5m),
        level: errors5m === 0 ? "ok" : errors5m < 10 ? "warn" : "crit",
        hint: errors5m === 0 ? "Keine Serverfehler." : "Logs prüfen: docker compose logs web",
      },
      {
        label: "Postgres-Pool",
        value: `${data.pool.total - data.pool.idle}/${data.pool.max} belegt${data.pool.waiting > 0 ? ` · ${data.pool.waiting} wartend` : ""}`,
        level: data.pool.waiting > 0 ? "crit" : data.pool.idle === 0 && data.pool.total >= data.pool.max ? "warn" : "ok",
        hint: data.pool.waiting > 0
          ? "Requests warten auf DB-Verbindungen → PG_POOL_MAX erhöhen oder web skalieren."
          : "Verbindungen verfügbar.",
      },
      {
        label: "Event-Loop-Lag (schlechtestes Replica)",
        value: `${worstLag} ms`,
        level: worstLag < 50 ? "ok" : worstLag < 200 ? "warn" : "crit",
        hint: worstLag < 50
          ? "CPU entspannt."
          : "Node-Prozess unter Druck → docker compose up -d --scale web=3",
      },
      {
        label: "RAM (schlechtestes Replica)",
        value: `${worstRss} MB`,
        level: worstRss < 1024 ? "ok" : worstRss < 1536 ? "warn" : "crit",
        hint: worstRss < 1024 ? "Speicher im Rahmen." : "Speicher beobachten — ggf. Container-Neustart einplanen.",
      },
    ];

    return { last, throttled5m, errors5m, peak, limits };
  }, [data]);

  if (!data || !derived) {
    return (
      <section className="bg-card rounded-lg shadow-card p-8 text-center text-sm text-muted-foreground">
        {loading ? "Lade Metriken …" : "Keine Daten."}
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-display font-bold text-lg flex items-center gap-2">
          <Activity className="h-5 w-5" /> Live-Monitor
        </h2>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground">
            Auto-Refresh 10s · {new Date(data.generatedAt).toLocaleTimeString("de-AT")}
          </span>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} className="h-8">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <StatCard icon={<Users className="h-4 w-4" />} label="Aktive Nutzer (5 min)" value={data.activeUsers5m} accent />
        <StatCard icon={<Activity className="h-4 w-4" />} label="Requests/min" value={derived.last?.total ?? 0} sub={`Ø ${derived.last?.avgMs ?? 0} ms`} />
        <StatCard icon={<Radio className="h-4 w-4" />} label="SSE-Verbindungen" value={data.sseTotal} sub={`${data.replicas.length} Replica${data.replicas.length === 1 ? "" : "s"}`} />
        <StatCard
          icon={<AlertTriangle className="h-4 w-4" />}
          label="Drosselungen (5 min)"
          value={derived.throttled5m}
          danger={derived.throttled5m > 0}
        />
      </div>

      {/* Request sparkline — last 30 min, pure CSS bars */}
      <div className="bg-card rounded-lg shadow-card p-4">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">API-Requests · letzte 30 min</div>
          <div className="text-[10px] text-muted-foreground">
            <span className="inline-block w-2 h-2 bg-accent rounded-sm mr-1" />OK
            <span className="inline-block w-2 h-2 bg-destructive rounded-sm ml-3 mr-1" />429/5xx
          </div>
        </div>
        <div className="flex items-end gap-[2px] h-24">
          {data.series.map((b) => {
            const bad = b.s429 + b.s5xx;
            const hTotal = Math.round((b.total / derived.peak) * 100);
            const hBad = b.total > 0 ? Math.round((bad / b.total) * hTotal) : 0;
            return (
              <div
                key={b.minute}
                className="flex-1 flex flex-col justify-end h-full"
                title={`${new Date(b.minute * 60_000).toLocaleTimeString("de-AT", { hour: "2-digit", minute: "2-digit" })} · ${b.total} req · ${bad} Fehler/429 · Ø ${b.avgMs} ms`}
              >
                <div className="w-full bg-destructive rounded-t-[2px]" style={{ height: `${hBad}%` }} />
                <div className="w-full bg-accent/70 rounded-t-[2px]" style={{ height: `${Math.max(b.total > 0 ? 2 : 0, hTotal - hBad)}%` }} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Limit gauges */}
      <div className="bg-card rounded-lg shadow-card p-4 space-y-2">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">Limits & Engpässe</div>
        {derived.limits.map((l) => (
          <div key={l.label} className={`flex items-center gap-3 rounded-md border px-3 py-2 ${levelCls(l.level)}`}>
            {l.level === "ok" ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <AlertTriangle className="h-4 w-4 shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-bold">{l.label}</div>
              <div className="text-[11px] opacity-80">{l.hint}</div>
            </div>
            <div className="font-display font-extrabold text-sm tabular-nums shrink-0">{l.value}</div>
          </div>
        ))}
        {!data.redisOk && (
          <div className={`flex items-center gap-3 rounded-md border px-3 py-2 ${levelCls("warn")}`}>
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <div className="text-xs font-bold flex-1">Redis nicht verbunden — Metriken nur für dieses Replica, Caches inaktiv.</div>
          </div>
        )}
      </div>

      {/* Per-replica detail */}
      <div className="bg-card rounded-lg shadow-card p-4">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
          <Cpu className="h-3.5 w-3.5" /> Replicas
        </div>
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-left py-1">Instanz</th>
              <th className="text-right py-1">SSE</th>
              <th className="text-right py-1">RAM</th>
              <th className="text-right py-1">EL-Lag</th>
              <th className="text-right py-1">Uptime</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.replicas.map((rep) => (
              <tr key={rep.id}>
                <td className="py-1.5 font-mono truncate max-w-[180px]" title={rep.id}>{rep.id}</td>
                <td className="py-1.5 text-right tabular-nums">{rep.sse}</td>
                <td className="py-1.5 text-right tabular-nums">{rep.rssMb} MB</td>
                <td className="py-1.5 text-right tabular-nums">{rep.elLagMs} ms</td>
                <td className="py-1.5 text-right tabular-nums">{Math.floor(rep.uptimeSec / 3600)}h {Math.floor((rep.uptimeSec % 3600) / 60)}m</td>
              </tr>
            ))}
            {data.replicas.length === 0 && (
              <tr><td colSpan={5} className="py-3 text-center text-muted-foreground">Keine Heartbeats — Container gerade gestartet?</td></tr>
            )}
          </tbody>
        </table>
        <p className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1">
          <Database className="h-3 w-3" /> Skalieren am Spieltag: <code className="bg-muted px-1 rounded">docker compose up -d --scale web=3</code>
        </p>
      </div>
    </section>
  );
}

function StatCard({ icon, label, value, sub, accent, danger }: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  sub?: string;
  accent?: boolean;
  danger?: boolean;
}) {
  return (
    <div className={`rounded-lg p-3 shadow-card ${danger ? "bg-destructive text-destructive-foreground" : accent ? "bg-accent text-accent-foreground" : "bg-card"}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider opacity-70">
        {icon} {label}
      </div>
      <div className="font-display font-extrabold text-2xl tabular-nums mt-1">{value}</div>
      {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}
